import {
  ChatResponseAppointmentList,
  type ChatResponseAppointmentListItem,
} from "@indigo-components/components/Chat";
import type { StatusChipVariant } from "@indigo-components/components/StatusChip/StatusChip";
import type { FC } from "react";
import type {
  AppointmentCard,
  AppointmentListBlock as AppointmentListBlockData,
} from "~/pydantic_models/services/models/chat_blocks";
import type { ChatBlockRendererProps } from "./types";

/**
 * `appointment_list` → `ChatResponseAppointmentList`.
 *
 * The payload is display-shaped on purpose (built by
 * `services/agent/tools/render_appointment_list.py`): titles, subtitles and
 * status labels arrive finished, so nothing is re-derived here. Rows open
 * the appointment in a new tab, like an in-app link in the prose, so the
 * conversation stays put.
 */

const STATUS_CHIP_VARIANTS: readonly StatusChipVariant[] = [
  "todo",
  "done",
  "error",
  "final",
  "processing",
];

/** Backend and design system agree on these names; a value from a newer
 * backend falls back to the neutral variant rather than crashing. */
function toStatusVariant(value: string | null | undefined): StatusChipVariant {
  return STATUS_CHIP_VARIANTS.includes(value as StatusChipVariant)
    ? (value as StatusChipVariant)
    : "todo";
}

/**
 * Network payload — validate the shape we actually read before trusting it.
 *
 * The types come from `services/models/chat_blocks.py` via
 * `generate_ts_models.sh`, so a renamed or retyped field on the backend
 * breaks the build here instead of silently emptying a card. The runtime
 * check stays because the payload still arrives as untyped JSON: an older
 * row or a newer backend can carry a shape this bundle's types don't
 * describe.
 */
function parseAppointmentList(
  data: Record<string, unknown> | null | undefined,
): AppointmentListBlockData | null {
  if (!data || !Array.isArray(data.appointments)) return null;
  const appointments: AppointmentCard[] = [];
  for (const raw of data.appointments) {
    const card = raw as Partial<AppointmentCard> | null;
    if (
      !card ||
      typeof card.id !== "string" ||
      typeof card.title !== "string"
    ) {
      return null;
    }
    appointments.push(card as AppointmentCard);
  }
  return {
    header: typeof data.header === "string" ? data.header : null,
    appointments,
  };
}

export const AppointmentListBlock: FC<ChatBlockRendererProps> = ({
  block,
  fallback,
}) => {
  const parsed = parseAppointmentList(block.data);
  if (!parsed || parsed.appointments.length === 0) return <>{fallback}</>;

  const items: ChatResponseAppointmentListItem[] = parsed.appointments.map(
    (card) => ({
      id: card.id,
      title: card.title,
      subtitle: card.subtitle ?? undefined,
      status: card.status_label
        ? {
            label: card.status_label,
            variant: toStatusVariant(card.status_variant),
          }
        : undefined,
      onClick: () =>
        window.open(
          `/appointments/${card.id}`,
          "_blank",
          "noopener,noreferrer",
        ),
    }),
  );

  return (
    <ChatResponseAppointmentList
      header={parsed.header ?? undefined}
      appointments={items}
      data-testid="chat-appointment-list"
    />
  );
};
